import React, { useEffect, useState, useRef } from 'react';
import latteArt from '../coffeetexture.png'; // Updated image path

function IntroBlock() {
  const lines = [
    "Give Your Wallet a Break",
    "One App Every Cafe",
    "Earn Rewards on Every Sip",
    "Join the Coffee Revolution"
  ];  

  const [phases, setPhases] = useState(lines.map(() => 'initial'));
  const [isMobile, setIsMobile] = useState(false);
  const [showCTA, setShowCTA] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    lines.forEach((_, index) => {
      if (index === lines.length - 1) {
        // Last line: slide -> stay
        setTimeout(() => updatePhase(index, 'slide'), index * 2500);
      } else {
        // Others: slide -> flyoff
        setTimeout(() => updatePhase(index, 'slide'), index * 2500);
        setTimeout(() => updatePhase(index, 'flyoff'), index * 2500 + 2000);
      }
    });

    // Show CTA after the last line has slid in (adjust delay as needed)
    const lastIndex = lines.length - 1;
    setTimeout(() => setShowCTA(true), lastIndex * 2000 + 2000); // 1500ms after last slide starts
  }, []);

  const updatePhase = (index, newPhase) => {
    setPhases(prev => {
      const next = [...prev];
      next[index] = newPhase;
      return next;
    });
  };

  const getLineStyle = (phase) => {
    let baseStyle = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) translateY(100px)',
      fontSize: isMobile ? 'calc(1.2em + 2vw)' : 'calc(1.5em + 2vw)', // Smaller on mobile
      color: '#FFFFFF', // White text
      fontFamily: "'Roboto Condensed', sans-serif", // Matching theme
      fontWeight: '900', // Bold
      textShadow: '0px 3px 0px #000000', // Enhanced shadow for grandeur
      margin: 0,
      opacity: 0,
      transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
      zIndex: phase !== 'initial' ? 10 : -1, // Bring to front when active
      whiteSpace: isMobile ? 'normal' : 'nowrap', // Allow wrapping on mobile
      maxWidth: '90vw', // Prevent overflow
      textAlign: 'center',
    };

    if (phase === 'slide') {
      baseStyle.opacity = 1;
      baseStyle.transform = 'translate(-50%, -50%) translateY(0) scale(1.2)';
      baseStyle.transition = 'opacity 1s ease-in-out, transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else if (phase === 'flyoff') {
      baseStyle.opacity = 0;
      baseStyle.transform = 'translate(-50%, -50%) translateX(100vw) scale(0.5)';
      baseStyle.transition = 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out';
    } else if (phase === 'stay') {
      baseStyle.opacity = 1;
      baseStyle.transform = 'translate(-50%, -50%) translateY(0) scale(1.2)';
      baseStyle.transition = 'opacity 1.5s ease-in-out, transform 1.5s ease-in-out';
    }

    return baseStyle;
  };

  const blockRef = useRef(null);
  const backgroundRef = useRef(null);

  const handleScroll = () => {
    if (!blockRef.current || !backgroundRef.current) return;

    const blockTop = blockRef.current.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    
    // Check if the block is within the viewport
    if (blockTop < windowHeight && blockTop > -windowHeight) {
      const imageSpeed = 0.3; // Adjust as needed

      const translateY = (blockTop - windowHeight) * imageSpeed;
      backgroundRef.current.style.transform = `translate3d(0, ${translateY}px, 0)`;
    }
  };

  useEffect(() => {
    handleScroll(); // Initial call to set position
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const blockStyle = {
    height: '70vh',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  };

  const imgStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '200%',
    objectFit: 'cover',
    objectPosition: 'center',
    zIndex: 1,
    transform: 'translate3d(0, 0, 0)',
  };

  const ctaStyle = {
    position: 'absolute',
    bottom: '20%',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '12px 24px',
    backgroundColor: '#f48e28', // Matching your theme color
    boxShadow: '1px 4px 0px #CC7722',
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    fontSize: '18px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    opacity: showCTA ? 1 : 0,
    transition: 'opacity 1s ease-in-out',
    zIndex: 20,
    textDecoration: 'none',
  };

  return (
    <div ref={blockRef} style={blockStyle}>
      <img ref={backgroundRef} src={latteArt} alt="Coffee Texture" style={imgStyle} />
      {lines.map((line, index) => (
        <p key={index} style={getLineStyle(phases[index])}>
          {line}
        </p>
      ))}
      {showCTA && (
        <a 
          href="https://apps.apple.com/ca/app/beanbonus/id6479172588" 
          style={ctaStyle}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download on the App Store
        </a>
      )}
    </div>
  );
}

export default IntroBlock;